const SAVE_KEY = "sliding-puzzle-quest-save";
const BGM_AUTO_PLAY_KEY = "sliding-puzzle-quest-bgm-autoplay";
const SHOW_PLAY_TIMER_KEY = "sliding-puzzle-quest-show-play-timer";
const PLAY_SOUND_EFFECTS_KEY = "sliding-puzzle-quest-play-sound-effects";
const PLAY_TILE_MODE_KEY = "sliding-puzzle-quest-play-tile-mode";
const PLAY_PREFS_KEY_PREFIX = "sliding-puzzle-quest-play-prefs";

const VALID_TILE_MODES = new Set(["image", "numbers"]);

function playPrefsStorageKey(userId) {
    const id = String(userId || "").trim();
    return id ? `${PLAY_PREFS_KEY_PREFIX}-${id}` : null;
}

const VALID_PLAY_DIFFICULTIES = ["easy", "medium", "hard"];

/** @returns {{ gridSize: number, difficulty: string } | null} */
export function loadPlayGridPreferences(userId) {
    const key = playPrefsStorageKey(userId);
    if (!key) return null;
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const data = JSON.parse(raw);
        const gridSize = Number(data?.gridSize);
        const difficulty = String(data?.difficulty || "").toLowerCase();
        if (!Number.isFinite(gridSize) || gridSize < 2) return null;
        if (!VALID_PLAY_DIFFICULTIES.includes(difficulty)) return null;
        return { gridSize, difficulty };
    } catch {
        return null;
    }
}

export function savePlayGridPreferences(userId, { gridSize, difficulty } = {}) {
    const key = playPrefsStorageKey(userId);
    if (!key) return;
    const gs = Number(gridSize);
    const difficultyNorm = String(difficulty || "").toLowerCase();
    if (!Number.isFinite(gs) || gs < 2) return;
    if (!VALID_PLAY_DIFFICULTIES.includes(difficultyNorm)) return;
    try {
        localStorage.setItem(
            key,
            JSON.stringify({ gridSize: gs, difficulty: difficultyNorm, savedAt: Date.now() }),
        );
    } catch {
        /* quota or private mode */
    }
}

/** @returns {boolean | null} — `null` when the user has not chosen locally yet. */
export function loadBgmAutoPlayPreference() {
    try {
        const raw = localStorage.getItem(BGM_AUTO_PLAY_KEY);
        if (raw === "true") return true;
        if (raw === "false") return false;
        return null;
    } catch {
        return null;
    }
}

export function saveBgmAutoPlayPreference(enabled) {
    try {
        localStorage.setItem(BGM_AUTO_PLAY_KEY, enabled ? "true" : "false");
    } catch {
        /* quota or private mode */
    }
}

/**
 * Tile / line-match SFX while playing.
 * @returns {boolean | null} — `null` when the user has not chosen locally yet.
 */
export function loadPlaySoundEffectsPreference() {
    try {
        const raw = localStorage.getItem(PLAY_SOUND_EFFECTS_KEY);
        if (raw === "true") return true;
        if (raw === "false") return false;
        return null;
    } catch {
        return null;
    }
}

export function savePlaySoundEffectsPreference(enabled) {
    try {
        localStorage.setItem(PLAY_SOUND_EFFECTS_KEY, enabled ? "true" : "false");
    } catch {
        /* quota or private mode */
    }
}

/** Resolved BGM preference (local override, else default on). */
export function resolveBgmAutoPlayPreference(fallback = true) {
    const local = loadBgmAutoPlayPreference();
    return local === null ? Boolean(fallback) : local;
}

/** Resolved SFX preference (local override, else default on). */
export function resolvePlaySoundEffectsPreference(fallback = true) {
    const local = loadPlaySoundEffectsPreference();
    return local === null ? Boolean(fallback) : local;
}

/** @returns {boolean} — whether the play screen should show the live timer. Default off. */
export function loadShowPlayTimerPreference() {
    try {
        const raw = localStorage.getItem(SHOW_PLAY_TIMER_KEY);
        if (raw === "true") return true;
        if (raw === "false") return false;
        return false;
    } catch {
        return false;
    }
}

export function saveShowPlayTimerPreference(enabled) {
    try {
        localStorage.setItem(SHOW_PLAY_TIMER_KEY, enabled ? "true" : "false");
    } catch {
        /* quota or private mode */
    }
}

/**
 * Play board tile chrome: photo slices vs numbered tiles.
 * @returns {"image"|"numbers"|null}
 */
export function loadPlayTileModePreference() {
    try {
        const raw = String(localStorage.getItem(PLAY_TILE_MODE_KEY) || "").trim().toLowerCase();
        return VALID_TILE_MODES.has(raw) ? raw : null;
    } catch {
        return null;
    }
}

/** @param {"image"|"numbers"} mode */
export function savePlayTileModePreference(mode) {
    const next = String(mode || "").trim().toLowerCase();
    if (!VALID_TILE_MODES.has(next)) return;
    try {
        localStorage.setItem(PLAY_TILE_MODE_KEY, next);
    } catch {
        /* quota or private mode */
    }
}

/**
 * Opposite of the last saved tile mode (default baseline `"image"`).
 * Used so each 1080p content reload alternates image ↔ numbers.
 * @returns {"image"|"numbers"}
 */
export function takeAlternatingPlayTileMode() {
    const last = loadPlayTileModePreference();
    const next = last === "numbers" ? "image" : "numbers";
    savePlayTileModePreference(next);
    return next;
}

export function loadSavedGame() {
    try {
        const raw = localStorage.getItem(SAVE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || !Array.isArray(data.layout)) return null;
        return data;
    } catch {
        return null;
    }
}

export function saveGame(game) {
    try {
        localStorage.setItem(SAVE_KEY, JSON.stringify({ ...game, savedAt: Date.now() }));
    } catch {
        /* quota or private mode */
    }
}

export function clearSavedGame() {
    try {
        localStorage.removeItem(SAVE_KEY);
    } catch {
        /* ignore */
    }
}
